import { Table } from "react-bootstrap";
import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import TableSkeleton from "../../common/TableSkeleton";
import TableError from "../../common/TableError";
import "../../../../common/styles/table.css";

/**
 * @typedef {import("../../../../hooks/organization/report/useGetSubSchoolStudentsList").StudentReportItem} StudentReportItem
 */

/**
 * SubSchoolStudentTable component displays a list of students with their performance data
 * @param {object} props - Component props
 * @param {StudentReportItem[]} [props.students] - Array of student data
 * @param {boolean} [props.loading] - Loading state
 * @param {string} [props.error] - Error message
 * @param {object} [props.metaData] - Pagination metadata
 * @param {number} [props.currentPage] - Current page number
 * @param {Function} [props.setCurrentPage] - Function to update current page
 * @param {Function} [props.handleRefresh] - Function to refresh data
 * @param {Function} [props.onStudentClick] - Callback when a student row is clicked
 * @param {boolean} [props.isInitialLoad] - Whether this is the initial load
 * @returns {JSX.Element}
 */
const SubSchoolStudentTable = ({
  students = [],
  loading = false,
  error = "",
  metaData,
  currentPage = 1,
  setCurrentPage,
  handleRefresh,
  onStudentClick,
  isInitialLoad = false,
}) => {
  const totalPages = metaData?.last_page || 1;

  // Show skeleton only on initial load
  if (loading && isInitialLoad) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  // Show error state
  if (error) {
    return (
      <TableError
        error={error}
        handleRefresh={handleRefresh}
        loading={loading}
      />
    );
  }

  // Show empty state
  if (!students || students.length === 0) {
    return (
      <div className="alert alert-info mt-3" role="alert">
        No students found.
      </div>
    );
  }

  return (
    <div className="custom-table-container mt-3">
      <div className="custom-table-wrapper">
        <Table hover className="custom-table">
          <thead className="custom-table-head">
            <tr>
              <th>Student Name</th>
              <th>Progress</th>
              <th>Avg. Score</th>
              <th>Quests Assigned</th>
              <th>Lessons Completed</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student) => (
              <tr
                key={student.id}
                onClick={() => onStudentClick && onStudentClick(student)}
                style={{
                  cursor: onStudentClick ? "pointer" : "default",
                }}
              >
                <td>{student.name || "-"}</td>
                <td>{student.progress_percent ?? 0}%</td>
                <td>{student.avg_score ?? 0}</td>
                <td>{student.quests_assigned ?? 0}</td>
                <td>{student.lessons_completed || "-"}</td>
                <td>
                  <button className="custom-detail-btn">
                    <i className="bi bi-three-dots-vertical"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <TablePagination
        totalPage={totalPages}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        disabled={loading}
      />
    </div>
  );
};

export default SubSchoolStudentTable;
